import { AgentSidebarProvider, AgentSidebarScope, useAgentWorkspace } from "./AgentSidebarContext";
import { ChalkAssistantNudge } from "./ChalkAssistantNudge";
import { useRouteInfo } from "@/utils/hooks/url/use-route-info";
import { useScopeGeneration } from "@/utils/hooks/use-scope-generation";
import { useSession } from "next-auth/react";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import React, { PropsWithChildren } from "react";

const EnvironmentAgentSidebar = dynamic(
  () => import("./EnvironmentAgentSidebar").then((module) => module.EnvironmentAgentSidebar),
  { ssr: false },
);

export const ChalkAssistantWorkspace: React.FC<PropsWithChildren> = ({ children }) => {
  const router = useRouter();
  const { projectId, environmentId } = useRouteInfo();
  const { data: session } = useSession();
  // The workspace wraps the whole page, so its tree shape and key must hold while the route ids
  // (after hydration) and the session resolve; changing either repaints the page. The route
  // template says the page is environment-scoped before the ids arrive, and the key changes only
  // when a known project, environment or user is replaced.
  const workspaceKey = useScopeGeneration([projectId, environmentId, session?.user?.email]);
  if (environmentId == null && !router.pathname.includes("[environmentId]")) {
    return <>{children}</>;
  }
  return (
    <EnvironmentWorkspace key={workspaceKey} environmentId={environmentId}>
      {children}
    </EnvironmentWorkspace>
  );
};

const EnvironmentWorkspace: React.FC<PropsWithChildren<{ environmentId: string | undefined }>> = ({
  children,
  environmentId,
}) => {
  const available = environmentId != null;
  return (
    <AgentSidebarProvider available={available} environmentId={environmentId}>
      {children}
      {available && <AssistantSessions />}
      {available && <ChalkAssistantNudge />}
    </AgentSidebarProvider>
  );
};

const AssistantSessions = () => {
  const workspace = useAgentWorkspace();
  return workspace?.state.chats.map((chat) => <AssistantSession key={chat.id} chatId={chat.id} />);
};

// Rail focus and geometry changes should render the shell, not every chat transcript.
const AssistantSession = React.memo(function AssistantSession({ chatId }: { chatId: string }) {
  return (
    <AgentSidebarScope chatId={chatId}>
      <EnvironmentAgentSidebar chatId={chatId} />
    </AgentSidebarScope>
  );
});
